import classNames from 'classnames';
import React, { useEffect, useImperativeHandle, useRef, type ChangeEventHandler, type ForwardedRef } from 'react';
import Styles from './Input.module.scss';
import { DobLabel } from '@dedalusdiit/dob-components';

export interface InputProps {
    label: string;
    value: string;
    disabled?: boolean;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    onBlur?: ChangeEventHandler<HTMLInputElement>;
    hasValidationError?: boolean;
    onEnter?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    onFocus?: () => void;
    onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    light?: boolean;
    small?: boolean;
    className?: string;
}

const Input = React.forwardRef((props: Readonly<InputProps>, ref: ForwardedRef<HTMLInputElement>): React.ReactElement => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const { className, label, value, onChange, onBlur, onEnter, onFocus, onKeyUp, disabled: isDisabled, hasValidationError, light, small } = props;

    // This is used to create a combination of "internal" ref usage for the input field, but also allow the forwardRef to do its thing
    // @ts-ignore
    useImperativeHandle(ref, () => inputRef.current);

    const toggleFocus = (focussed: boolean): void => {
        if (!wrapperRef.current || value !== '') {
            return;
        }

        if (!focussed && wrapperRef.current.classList.contains(Styles.isFocussed)) {
            wrapperRef.current.classList.remove(Styles.isFocussed);
        }
        else if (focussed && !wrapperRef.current.classList.contains(Styles.isFocussed)) {
            wrapperRef.current.classList.add(Styles.isFocussed);
            onFocus?.();
        }
    };

    const setFocus = (): void => {
        if (!wrapperRef.current || inputRef.current === document.activeElement && wrapperRef.current.classList.contains(Styles.isFocussed)) {
            return;
        }

        if (value === '') {
            wrapperRef.current.classList.remove(Styles.isFocussed);
        }
        else {
            wrapperRef.current.classList.add(Styles.isFocussed);
        }
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === 'Enter') {
            onEnter?.(e);
        }
    };

    useEffect(setFocus, [value]);

    const classes = classNames(Styles.wrapper, {
        [Styles.lightBottom]: light,
        [Styles.small]: small,
        [Styles.hasValidationError]: hasValidationError,
    });

    const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
        if (onBlur) {
            onBlur(e);
        }

        toggleFocus(false);
    };

    return (
        <div ref={wrapperRef} className={classes}>
            <DobLabel text={label} />
            <input
                className={className}
                ref={inputRef}
                value={value}
                onChange={(e) => onChange?.(e)}
                onFocus={(_) => toggleFocus(true)}
                onBlur={handleOnBlur}
                onKeyDown={onKeyDown}
                onKeyUp={(e) => onKeyUp?.(e)}
                disabled={isDisabled ?? false}
            />
        </div>
    );
});

Input.displayName = 'Input'; // Necessary because of the forwardRef usage, which can confuse ESLint. This is meant to set the name of the component in (for example) the React Devtools

export default Input;
